import { JsonObject, JsonProperty } from 'json2typescript';
import { Moment } from 'moment';

import {
    ClientSignatureType,
    IdInterface,
    OrderClientStatus,
    OrderType,
    ProductSpecification,
    SelectedProductInterface,
    SelectedProductStatus,
} from '../../../core';
import { Id } from './id';
import { IdAdapter } from '../../client/helpers/serializer/adapters/id.adapter';
import { Order } from './order';
import { OrderDocument } from './order-document';
import { SelectedProductStatusAdapter } from '../../client/helpers/serializer/adapters/selected-product-status.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';
import { StringToNumberAdapter } from '../../client/helpers/serializer/adapters/string-to-number.adapter';
import { YesNoToBooleanAdapter } from '../../client/helpers/serializer/adapters/yes-no-to-boolean.adapter';
import { MeetingDetails } from './meeting-details';

@JsonObject('SelectedProduct')
export class SelectedProduct implements SelectedProductInterface {
    @JsonProperty('ListID', IdAdapter, true)
    private _id: Id = undefined;

    @JsonProperty('ContractCode', IdAdapter, true)
    private _contractCode: IdInterface = undefined;

    @JsonProperty('ProductID', IdAdapter, true)
    private _productId: Id = undefined;

    @JsonProperty('ProductCode', IdAdapter, true)
    private _code: Id = undefined;

    @JsonProperty('Code', IdAdapter, true)
    private _encryptedCode: Id = undefined;

    @JsonProperty('Isin', StringAdapter, true)
    private _isin: string = undefined;

    @JsonProperty('Name', StringAdapter, true)
    private _name: string = undefined;

    private _productSpecification: ProductSpecification = undefined;

    private _lastPrice: number = undefined;

    private _lastPriceDate: Moment = undefined;


    @JsonProperty('IsAppropriateness', YesNoToBooleanAdapter, true)
    private _isAppropriateness: boolean = undefined;

    @JsonProperty('IsAutomatic', Boolean, true)
    private _isAutomatic: boolean = undefined;

    @JsonProperty('IsExonerated', Boolean, true)
    private _isExonerated: boolean = undefined;

    @JsonProperty('Status', SelectedProductStatusAdapter, true)
    private _status: SelectedProductStatus = undefined;

    @JsonProperty('Order', Order, true)
    private _order: Order = undefined;

    @JsonProperty('MeetingDetails', MeetingDetails, true)
    private _meetingDetails: MeetingDetails = undefined;

    @JsonProperty('FundAmmount', Number, true)
    private _amount: number = undefined;

    @JsonProperty('FundSharesPortfolio', StringToNumberAdapter, true)
    private _shares: number = undefined;

    private _documents: OrderDocument[] = undefined;

    private _selected = true;

    constructor(contractCode?: IdInterface) {
        this._contractCode = contractCode;
        this._isAppropriateness = true;
        this._isExonerated = false;
        this._lastPrice = 0;
        this._amount = 0;
        this._shares = 0;
        this._status = SelectedProductStatus.DRAFT;
    }

    get id(): Id {
        return this._id;
    }

    get productId(): Id {
        return this._productId;
    }

    set productId(value: Id) {
        this._productId = value;
    }

    get code(): Id {
        return this._code;
    }

    set code(value: Id) {
        this._code = value;
    }

    get encryptedCode(): Id {
        return this._encryptedCode;
    }

    set encryptedCode(value: Id) {
        this._encryptedCode = value;
    }

    get isin(): string {
        return this._isin;
    }

    set isin(value: string) {
        this._isin = value;
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get productSpecification(): ProductSpecification {
        return this._productSpecification;
    }

    set productSpecification(value: ProductSpecification) {
        this._productSpecification = value;
    }

    get lastPrice(): number {
        return this._lastPrice;
    }

    set lastPrice(value: number) {
        this._lastPrice = value;
    }

    get lastPriceDate(): Moment {
        return this._lastPriceDate;
    }

    set lastPriceDate(value: Moment) {
        this._lastPriceDate = value;
    }

    get isAppropriateness(): boolean {
        return this._isAppropriateness;
    }

    get isAutomatic(): boolean {
        return this._isAutomatic;
    }

    get isExonerated(): boolean {
        return this._isExonerated;
    }

    set isExonerated(value: boolean) {
        this._isExonerated = value;
    }

    get contractCode(): IdInterface {
        return this._contractCode;
    }

    get status(): SelectedProductStatus {
        if (this._status === SelectedProductStatus.ACTIVE && this.order.canceled) {
            return SelectedProductStatus.CANCELED;
        }

        return this._status;
    }

    get order(): Order {
        if (!this._order) {
            this._order = new Order();
        }

        return this._order;
    }

    set order(value: Order) {
        this._order = value;
    }

    get meetingDetails(): MeetingDetails {
       if (!this._meetingDetails) {
            this._meetingDetails = new MeetingDetails();
        }

        return this._meetingDetails;
    }

    set meetingDetails(value: MeetingDetails) {
        this._meetingDetails = value;
    }

    get amount(): number {
        return this._amount ? Number(this._amount.toFixed(2)) : 0;
    }

    set amount(value: number) {
        this._amount = value;
    }

    get shares(): number {
        return this._shares ? Number(this._shares.toFixed(8)) : 0;
    }

    set shares(value: number) {
        this._shares = value;
    }

    get documents(): OrderDocument[] {
        return this._documents;
    }

    set documents(value: OrderDocument[]) {
        this._documents = value;
    }

    get selected(): boolean {
        return this._selected;
    }

    get hasElectronicSign(): boolean {
        return (
            this.order &&
            this.order.clients &&
            this.order.clients.some(
                (client) =>
                    client.status === OrderClientStatus.PENDING &&
                    [ClientSignatureType.IN_PERSON_ELECTRONIC, ClientSignatureType.REMOTE_ELECTRONIC].indexOf(client.signatureType) !== -1
            )
        );
    }

    get hasHandwrittenSign(): boolean {
        return (
            this.order &&
            this.order.clients &&
            this.order.clients.some(
                (client) => client.status === OrderClientStatus.PENDING && client.signatureType === ClientSignatureType.HANDWRITTEN
            )
        );
    }

    isSubscription(): boolean {
        return this.order.type === OrderType.SUBSCRIPTION_PERIOD || this.order.type === OrderType.REFUND_PERIOD;
    }

    isSubscriptionActive(): boolean {
        return this.isSubscription() && this.status === SelectedProductStatus.ACTIVE;
    }
}
